import React, { useState, useEffect } from "react";
import { Typography, Radio, Textarea, Input } from "@material-tailwind/react";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import LawyerFeedbackForm from "./LawyerFeedbackForm";
import { currencyMask } from "../utils/helpers";
import { ValidationMessages } from "../utils/validationMessages";
import {
  formatConciliationReport,
  isConciliationReportValid,
} from "../utils/hearingReportValidation";

interface ConciliationHearingReportProps {
  onChange: (reportData: any) => void;
  includeLawyerFeedback?: boolean;
}

const ConciliationHearingReport: React.FC<ConciliationHearingReportProps> = ({
  onChange,
  includeLawyerFeedback = true,
}) => {
  const [hadConciliation, setHadConciliation] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    // Fields for positive conciliation
    totalAmount: "",
    installments: "",
    paymentMethod: "",
    observations: "",

    // Fields for negative conciliation
    maxCompanyProposal: "",
    minAcceptedProposal: "",
    hadCourtProposal: null,
    inHearingMinutes: null,
  });

  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [isValid, setIsValid] = useState(false);
  const [lawyerFeedbackData, setLawyerFeedbackData] = useState<any>(null);

  useEffect(() => {
    const isFormValid = isConciliationReportValid(hadConciliation, formData);
    setIsValid(
      includeLawyerFeedback
        ? isFormValid && !!lawyerFeedbackData?.isValid
        : isFormValid
    );
  }, [formData, hadConciliation, includeLawyerFeedback, lawyerFeedbackData]);

  useEffect(() => {
    if (hadConciliation && formData && (!includeLawyerFeedback || lawyerFeedbackData)) {
      // Create the formatted data to send to parent
      const formattedData = includeLawyerFeedback
        ? {
            ...formatConciliationReport({
              hadConciliation,
              ...formData,
            }),
            ...lawyerFeedbackData?.formattedData,
          }
        : {
            ...formatConciliationReport({
              hadConciliation,
              ...formData,
            }),
          };

      // Send both raw data and formatted data to parent
      onChange({
        hadConciliation,
        ...formData,
        ...(includeLawyerFeedback ? { lawyerFeedbackData } : {}),
        isValid,
        formattedData,
      });
    }
  }, [formData, hadConciliation, isValid, lawyerFeedbackData, includeLawyerFeedback]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouchedFields({
      ...touchedFields,
      [name]: true,
    });

    if (
      ["totalAmount", "maxCompanyProposal", "minAcceptedProposal"].includes(
        name
      )
    ) {
      const formattedValue = currencyMask(value);
      e.target.value = formattedValue;
      const newFormData = { ...formData, [name]: formattedValue };
      setFormData(newFormData);
    } else {
      const newFormData = { ...formData, [name]: value };
      setFormData(newFormData);
    }
  };

  const handleRadioChange = (name: string, value: string) => {
    // Mark field as touched
    setTouchedFields({
      ...touchedFields,
      [name]: true,
    });

    if (name === "hadConciliation") {
      setHadConciliation(value);
    } else {
      const newFormData = { ...formData, [name]: value };
      setFormData(newFormData);
    }
  };

  // Helper for field validation
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!touchedFields[fieldName]) return false;

    return !formData[fieldName as keyof typeof formData];
  };

  // Required field marker
  const RequiredMark = () => <span className="text-red-500 ml-1">*</span>;

  return (
    <div className="space-y-3">
      <div className="mb-1">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Houve conciliação? <RequiredMark />
        </Typography>
        <div className="flex gap-4">
          <Radio
            name="hadConciliation"
            label="Sim"
            onChange={() => handleRadioChange("hadConciliation", "Sim")}
            checked={hadConciliation === "Sim"}
          />
          <Radio
            name="hadConciliation"
            label="Não"
            onChange={() => handleRadioChange("hadConciliation", "Não")}
            checked={hadConciliation === "Não"}
          />
        </div>
        {touchedFields["hadConciliation"] && !hadConciliation && (
          <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
            <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
              {ValidationMessages.HEARING_REPORT.CONCILIATION_REQUIRED}
            </Typography>
          </div>
        )}
      </div>

      {hadConciliation === "Sim" && (
        <div className="space-y-4">
          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Valor total do acordo <RequiredMark />
            </Typography>
            <Input
              type="text"
              name="totalAmount"
              value={formData.totalAmount}
              onChange={handleInputChange}
              placeholder="R$ 0,00"
              className={`w-full ${
                isFieldInvalid("totalAmount") ? "border-red-500" : ""
              }`}
            />
            {isFieldInvalid("totalAmount") && (
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
                <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                  {ValidationMessages.HEARING_REPORT.AGREEMENT_AMOUNT_REQUIRED}
                </Typography>
              </div>
            )}
          </div>

          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Quantidade de parcelas <RequiredMark />
            </Typography>
            <Input
              type="number"
              name="installments"
              value={formData.installments}
              onChange={handleInputChange}
              className={`w-full ${
                isFieldInvalid("installments") ? "border-red-500" : ""
              }`}
            />
            {isFieldInvalid("installments") && (
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
                <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                  {ValidationMessages.HEARING_REPORT.INSTALLMENTS_REQUIRED}
                </Typography>
              </div>
            )}
          </div>

          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Forma de pagamento <RequiredMark />
            </Typography>
            <Input
              type="text"
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              maxLength={50}
              className={`w-full ${
                isFieldInvalid("paymentMethod") ? "border-red-500" : ""
              }`}
            />
            {isFieldInvalid("paymentMethod") && (
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
                <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                  {ValidationMessages.HEARING_REPORT.PAYMENT_METHOD_REQUIRED}
                </Typography>
              </div>
            )}
          </div>

          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Observações
            </Typography>
            <Textarea
              name="observations"
              value={formData.observations}
              onChange={handleInputChange}
              maxLength={1000}
              className="w-full"
            />
            <Typography className={`text-xs text-right mt-1 ${formData.observations.length >= 1000 ? 'text-red-500 font-medium' : 'text-gray-400'}`} placeholder="">
              {formData.observations.length}/1000 caracteres
            </Typography>
          </div>
        </div>
      )}

      {hadConciliation === "Não" && (
        <div className="space-y-4">
          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Valor da proposta máxima da empresa
            </Typography>
            <Input
              type="text"
              name="maxCompanyProposal"
              value={formData.maxCompanyProposal}
              onChange={handleInputChange}
              placeholder="R$ 0,00"
              className={`w-full`}
            />
          </div>

          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Proposta mínima aceita pela outra parte
            </Typography>
            <Input
              type="text"
              name="minAcceptedProposal"
              value={formData.minAcceptedProposal}
              onChange={handleInputChange}
              placeholder="R$ 0,00"
              className={`w-full `}
            />
          </div>

          <div>
            <Typography variant="small" className="text-blue-700 font-medium mb-1">
              Houve proposta do juízo? <RequiredMark />
            </Typography>
            <div className="flex gap-4">
              <Radio
                name="hadCourtProposal"
                label="Sim"
                onChange={() => handleRadioChange("hadCourtProposal", "Sim")}
                checked={formData.hadCourtProposal === "Sim"}
              />
              <Radio
                name="hadCourtProposal"
                label="Não"
                onChange={() => handleRadioChange("hadCourtProposal", "Não")}
                checked={formData.hadCourtProposal === "Não"}
              />
            </div>
            {touchedFields["hadCourtProposal"] &&
              !formData.hadCourtProposal && (
                <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
                  <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                    {ValidationMessages.HEARING_REPORT.COURT_PROPOSAL_REQUIRED}
                  </Typography>
                </div>
              )}
          </div>

          <div>
            <Typography variant="small" className="text-blue-700 font-medium mb-1">
              Essas informações constam em ata? <RequiredMark />
            </Typography>
            <div className="flex gap-4">
              <Radio
                name="inHearingMinutes"
                label="Sim"
                onChange={() => handleRadioChange("inHearingMinutes", "Sim")}
                checked={formData.inHearingMinutes === "Sim"}
              />
              <Radio
                name="inHearingMinutes"
                label="Não"
                onChange={() => handleRadioChange("inHearingMinutes", "Não")}
                checked={formData.inHearingMinutes === "Não"}
              />
            </div>
            {touchedFields["inHearingMinutes"] &&
              !formData.inHearingMinutes && (
                <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
                  <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                    {ValidationMessages.HEARING_REPORT.IN_HEARING_MINUTES_REQUIRED}
                  </Typography>
                </div>
              )}
          </div>

          <div>
            <Typography
              variant="small"
              className="text-blue-700 font-medium mb-1"
            >
              Observações (visível para a empresa contratante)
            </Typography>
            <Textarea
              name="observations"
              value={formData.observations}
              onChange={handleInputChange}
              maxLength={1000}
              className="w-full"
            />
            <Typography className={`text-xs text-right mt-1 ${formData.observations.length >= 1000 ? 'text-red-500 font-medium' : 'text-gray-400'}`} placeholder="">
              {formData.observations.length}/1000 caracteres
            </Typography>
          </div>
        </div>
      )}

      {/* Lawyer Feedback Section (optional) */}
      {includeLawyerFeedback && (
        <div className="mt-6 border-t pt-6">
          <LawyerFeedbackForm onChange={setLawyerFeedbackData} />
        </div>
      )}

      {hadConciliation && (
        <Typography className="text-xs text-gray-500 mt-4">
          Os campos marcados com <span className="text-red-500">*</span> são
          obrigatórios
        </Typography>
      )}
    </div>
  );
};

export default ConciliationHearingReport;
